import { CustomButton, PositionPropsState, SvgChevronUp } from '@ui-kit'
import { useEffect, useRef, useState } from 'react'
import { ShortcutKeys } from '../../constants/form.constants'
import { isEmpty } from 'lodash'
import { createPortal } from 'react-dom'

export const SelectButtonDropDown: React.FC<{
  dropdownItemList: { [key: string]: string }[]
  onOptionSelect: (selectedWorkflow: { [key: string]: string }) => void
  buttonText?: string
  disabled?: boolean
  isButtonOnTop?: boolean
  tabIndex?: number
  onDropDownClick?: () => void
  isPink?: boolean
}> = ({
  dropdownItemList = [],
  onOptionSelect,
  disabled,
  isButtonOnTop,
  tabIndex,
  onDropDownClick,
  isPink,
  buttonText,
}) => {
  const [popupData, setPopupData] = useState<PositionPropsState | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const [dropdownHeight, setDropdownHeight] = useState<string | undefined>(
    undefined
  )
  const [showTop, setShowTop] = useState(false)

  const itemRefs = useRef<Array<HTMLDivElement | null>>([])
  dropdownItemList.sort((a, b) => {
    if (!a.name) return -1
    if (!b.name) return 1
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
  })
  const handleButtonClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent
  ) => {
    const target = e?.currentTarget as HTMLDivElement
    const buttonRect = target?.getBoundingClientRect()
    const maxDropdownHeight = 328
    const availableSpaceBelow = window.innerHeight - buttonRect.bottom
    const availableSpaceAbove = buttonRect.top

    let showAbove = false
    let computedHeight = maxDropdownHeight
    if (
      availableSpaceBelow < maxDropdownHeight &&
      availableSpaceAbove > availableSpaceBelow
    ) {
      showAbove = true
      computedHeight = Math.min(availableSpaceAbove - 10, maxDropdownHeight)
    } else if (availableSpaceBelow < maxDropdownHeight) {
      computedHeight = Math.min(availableSpaceBelow - 10, maxDropdownHeight)
    }
    setShowTop(showAbove)
    const top =
      (showAbove ? window.innerHeight - buttonRect.y : buttonRect.bottom) +
      window.scrollY

    const popupPosition: PositionPropsState = {
      top,
      right: buttonRect.right - window.scrollX,
      width: buttonRect.width,
    }

    setPopupData({ ...popupPosition })
    setDropdownHeight(`${computedHeight}px`)
    setFocusedIndex(0)
    onDropDownClick && onDropDownClick()
  }

  const clearData = () => {
    setPopupData(null)
    setFocusedIndex(-1)
  }

  const handleSelect = (item: { [key: string]: string }) => {
    onOptionSelect(item)
    clearData()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ShortcutKeys.ArrowDown) {
      e.preventDefault()
      setFocusedIndex((prev) =>
        prev < dropdownItemList.length - 1 ? prev + 1 : 0
      )
    } else if (e.key === ShortcutKeys.ArrowUp) {
      e.preventDefault()
      setFocusedIndex((prev) =>
        prev > 0 ? prev - 1 : dropdownItemList.length - 1
      )
    } else if (e.key === ShortcutKeys.Enter || e?.key === ShortcutKeys.Space) {
      e.preventDefault()
      if (focusedIndex >= 0) handleSelect(dropdownItemList[focusedIndex])
      else {
        handleButtonClick(e)
      }
    } else if (
      e?.key === ShortcutKeys.Shift ||
      e?.shiftKey ||
      e?.key === ShortcutKeys.Tab
    ) {
      clearData()
      return
    } else {
      e.preventDefault()
      clearData()
    }
  }

  const getChevronColor = (
    isButtonOnTop: boolean | undefined,
    disabled: boolean | undefined
  ): 'primary' | 'error' | 'others' | 'cyan' | 'disabled' => {
    if (!isButtonOnTop) return 'primary'
    if (disabled) return 'disabled'
    return 'cyan'
  }

  const getChevronFillClass = (
    isButtonOnTop: boolean | undefined,
    disabled: boolean | undefined
  ): string => {
    if (!isButtonOnTop) return 'svg-fill-path-primary'
    return disabled ? 'svg-path-fill-accent-900' : 'svg-path-fill-accent-0'
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        clearData()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const handleScroll = (e: Event) => {
      if (e?.target !== dropdownRef?.current) {
        setPopupData(null)
      }
    }
    if (popupData) {
      window.addEventListener('scroll', handleScroll, true)
    }
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [popupData])

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      })
    }
  }, [focusedIndex])
  return (
    <div
      ref={buttonRef}
      className="flex items-stretch relative"
      onClick={isEmpty(popupData) ? handleButtonClick : clearData}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex ?? 0}
      aria-haspopup="listbox"
      aria-expanded={!!popupData}
      role="button"
    >
      <CustomButton
        type="button"
        color={getChevronColor(isButtonOnTop, disabled)}
        widthClass={`${isButtonOnTop ? 'w-[10.5rem]' : 'w-[8.563rem]'}`}
        className={`border-l-none border-r-2 !h-[1.75rem] ${
          isButtonOnTop ? '!h-[2.37rem]' : ''
        } ${isPink ? 'bg-primary-500 text-white' : ''}`}
        dataQaId="create-new-case"
        disabled={isButtonOnTop ? disabled : false}
        isFilled={isButtonOnTop}
        hcpPrimaryColor={`${
          isButtonOnTop && !isPink ? 'bg-secondary-500' : ''
        } `}
        tabIndex={-1}
      >
        <span className="flex items-center gap-2">
          {buttonText}
          <SvgChevronUp
            className={`transition-transform duration-300 ${
              popupData ? 'rotate-180' : 'rotate-90'
            } pointer-events-none ${getChevronFillClass(
              isButtonOnTop,
              disabled
            )}`}
          />
        </span>
      </CustomButton>
      {popupData &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`absolute z-[9999]  bg-accent-50 border-l border-r border-b rounded-b flex flex-col overflow-y-auto transition-all duration-300 ${
              popupData
                ? 'scale-y-100 opacity-100'
                : 'scale-y-0 opacity-0 pointer-events-none'
            }`}
            style={{
              ...(!showTop
                ? { top: popupData?.top }
                : { bottom: popupData?.top }),
              right: `calc(100vw - ${popupData.right}px)`,
              minWidth: `${popupData?.width}px`,
              position: 'absolute',
              maxHeight: dropdownHeight,
            }}
          >
            {dropdownItemList?.map((item, index) => (
              <div
                className={`p-3 hover:bg-secondary-100  ${
                  focusedIndex === index
                    ? 'bg-secondary-100 font-semibold'
                    : 'hover:bg-secondary-100'
                }`}
                key={item?.id}
                ref={(el) => (itemRefs.current[index] = el)}
              >
                <span
                  title={item?.name ?? ''}
                  className="truncate block w-full cursor-pointer"
                  onClick={() => handleSelect(item)}
                >
                  {item?.name}
                </span>
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  )
}
